import { z } from 'zod';
export * from './admin.js';
export * from './feedback.js';

export const platformSchema = z.enum([
  'web',
  'wechat',
  'zhihu',
  'xiaohongshu',
  'weibo',
  'x',
  'bilibili',
  'youtube',
]);
export type Platform = z.infer<typeof platformSchema>;
// Data-only selector slots. They can narrow an already identified target; they
// cannot redefine identity, perform requests, or execute source-page code.
const ruleSelectorSchema = z
  .string()
  .trim()
  .min(1)
  .max(256)
  .refine(
    (value) =>
      !/[{};\\\u0000-\u001f]/.test(value) && !/:(?!scope\b)/i.test(value) && !/[()]/.test(value),
    'Only bounded CSS selectors and :scope are supported.',
  );
const ruleSelectorsSchema = z.array(ruleSelectorSchema).max(20).default([]);
export const ruleProfileSchema = z
  .object({
    enabled: z.boolean().default(true),
    contentSelectors: ruleSelectorsSchema,
    titleSelectors: ruleSelectorsSchema,
    authorSelectors: ruleSelectorsSchema,
    excludeSelectors: ruleSelectorsSchema,
    partialSelectors: ruleSelectorsSchema,
    imageSourceAttributes: z
      .array(
        z.enum(['data-src', 'data-original', 'data-actualsrc', 'data-lazy-src', 'src', 'srcset']),
      )
      .max(6)
      .default(['data-src', 'data-original', 'data-actualsrc', 'data-lazy-src', 'src', 'srcset']),
  })
  .strict();
export const ruleProfilesSchema = z
  .object({
    web: ruleProfileSchema.default({}),
    wechat: ruleProfileSchema.default({}),
    zhihu: ruleProfileSchema.default({}),
    xiaohongshu: ruleProfileSchema.default({}),
    weibo: ruleProfileSchema.default({}),
    x: ruleProfileSchema.default({}),
    bilibili: ruleProfileSchema.default({}),
    youtube: ruleProfileSchema.default({}),
  })
  .strict();
/** Sparse site patches deliberately have no field defaults. */
export const siteRuleProfileSchema = ruleProfileSchema
  .partial()
  .extend({
    articleSelectors: ruleSelectorsSchema.optional(),
  })
  .strict();
export const siteRuleSchema = z
  .object({
    hostname: z
      .string()
      .min(1)
      .max(253)
      .refine((value) => {
        try {
          return (
            value
              .split('.')
              .every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(label)) &&
            new URL('https://' + value).hostname === value
          );
        } catch {
          return false;
        }
      }, 'Use an exact normalized hostname.'),
    pathPrefix: z
      .string()
      .max(2048)
      .default('/')
      .refine((value) => {
        try {
          return (
            value.startsWith('/') &&
            !value.startsWith('//') &&
            !/[?#\\]/.test(value) &&
            new URL(value, 'https://rules.invalid').pathname === value
          );
        } catch {
          return false;
        }
      }, 'Use a normalized path without query or fragment.')
      .transform((value) => value.replace(/\/+$/, '') || '/'),
    profile: siteRuleProfileSchema,
  })
  .strict();
export const siteRulesSchema = z
  .array(siteRuleSchema)
  .max(100)
  .default([])
  .superRefine((sites, context) => {
    const seen = new Set<string>();
    sites.forEach((site, index) => {
      const key = site.hostname + '\n' + site.pathPrefix;
      if (seen.has(key))
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index],
          message: 'Duplicate hostname and path prefix.',
        });
      seen.add(key);
    });
  });
const ruleBundleFields = {
  version: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[A-Za-z0-9._-]+$/),
  hash: z.string().regex(/^[a-f0-9]{64}$/),
  profiles: ruleProfilesSchema,
};
export const rulesV1Schema = z
  .object({ schemaVersion: z.literal(1), engineCompatibility: z.literal(1), ...ruleBundleFields })
  .strict();
export const rulesV2Schema = z
  .object({
    schemaVersion: z.literal(2),
    engineCompatibility: z.literal(2),
    ...ruleBundleFields,
    sites: siteRulesSchema,
  })
  .strict();
export const rulesSchema = z.discriminatedUnion('schemaVersion', [rulesV1Schema, rulesV2Schema]);
export type RuleProfile = z.infer<typeof ruleProfileSchema>;
export type RuleProfiles = z.infer<typeof ruleProfilesSchema>;
export type SiteRuleProfile = z.infer<typeof siteRuleProfileSchema>;
export type SiteRule = z.infer<typeof siteRuleSchema>;
export type CaptureRuleBundle = z.infer<typeof rulesSchema>;
export const contentKindSchema = z.enum(['article', 'post', 'video']);
export const qualitySchema = z.enum(['complete', 'partial', 'link_only']);
export const collectionFilterStatusSchema = z.enum(['queued', 'running', 'completed', 'failed']);
const orderedFilterValues = <T extends [string, ...string[]]>(schema: z.ZodEnum<T>) =>
  z
    .array(schema)
    .min(1)
    .max(schema.options.length)
    .transform((values) => schema.options.filter((value) => values.includes(value)));
/** Values in one dimension match with OR; different dimensions combine with AND. */
export const collectionFilterConditionsSchema = z
  .object({
    platform: orderedFilterValues(platformSchema).optional(),
    kind: orderedFilterValues(contentKindSchema).optional(),
    status: orderedFilterValues(collectionFilterStatusSchema).optional(),
    quality: orderedFilterValues(qualitySchema).optional(),
  })
  .strict();
export type CollectionFilterConditions = z.infer<typeof collectionFilterConditionsSchema>;
/** Empty arrays have the same meaning as an omitted dimension. Unknown fields remain invalid. */
export function normalizeCollectionFilterConditions(input: unknown): CollectionFilterConditions {
  if (!input || typeof input !== 'object' || Array.isArray(input))
    return collectionFilterConditionsSchema.parse(input);
  return collectionFilterConditionsSchema.parse(
    Object.fromEntries(
      Object.entries(input).filter(
        ([key, value]) =>
          !['platform', 'kind', 'status', 'quality'].includes(key) ||
          (value !== undefined && !(Array.isArray(value) && value.length === 0)),
      ),
    ),
  );
}
export const collectionFilterScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('all') }).strict(),
  z.object({ kind: z.literal('favorites') }).strict(),
  z.object({ kind: z.literal('unfiled') }).strict(),
  z
    .object({
      kind: z.literal('folder'),
      id: z
        .string()
        .uuid()
        .transform((value) => value.toLowerCase()),
    })
    .strict(),
]);
export type CollectionFilterScope = z.infer<typeof collectionFilterScopeSchema>;
export function collectionFilterScopeKey(input: CollectionFilterScope): string {
  const scope = collectionFilterScopeSchema.parse(input);
  return scope.kind === 'folder' ? `folder:${scope.id}` : scope.kind;
}
export const collectionFilterPreferenceSchema = z
  .object({
    scope: collectionFilterScopeSchema,
    conditions: collectionFilterConditionsSchema,
    revision: z.number().int().nonnegative(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type CollectionFilterPreference = z.infer<typeof collectionFilterPreferenceSchema>;
export const collectionFilterPreferencesSchema = z
  .object({
    items: z.array(collectionFilterPreferenceSchema),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type CollectionFilterPreferences = z.infer<typeof collectionFilterPreferencesSchema>;
export const collectionFilterPreferenceInputSchema = z
  .object({
    scope: collectionFilterScopeSchema,
    conditions: collectionFilterConditionsSchema,
    revision: z.number().int().nonnegative(),
  })
  .strict();
export type CollectionFilterPreferenceInput = z.infer<typeof collectionFilterPreferenceInputSchema>;
export const warningSchema = z.object({ code: z.string(), message: z.string() });
export const documentAssetSchema = z.object({
  id: z.string().max(128),
  url: z.string().url().max(8192),
  uploadId: z.string().uuid().optional(),
  alt: z.string().max(2000).default(''),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  role: z.enum(['body', 'cover']).default('body'),
});
export const identityEvidenceSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('bilibili'),
      aid: z.string().regex(/^[1-9]\d{0,19}$/),
      bvid: z.string().regex(/^BV[0-9A-Za-z]{10}$/),
    })
    .strict(),
  z
    .object({
      kind: z.literal('wechat'),
      biz: z
        .string()
        .min(1)
        .max(200)
        .regex(/^[A-Za-z0-9_+\/=-]+$/),
      mid: z.string().regex(/^[1-9]\d{0,19}$/),
      idx: z.string().regex(/^[1-9]\d{0,2}$/),
      slug: z
        .string()
        .regex(/^[A-Za-z0-9_-]+$/)
        .max(200)
        .optional(),
    })
    .strict(),
]);
export const documentSchema = z.object({
  identityEvidence: identityEvidenceSchema.optional(),
  version: z.literal(1),
  url: z.string().url().max(8192),
  identity: z.string().max(8192),
  platform: platformSchema,
  kind: contentKindSchema,
  title: z.string().max(1000),
  author: z.string().max(500).nullable().default(null),
  siteName: z.string().max(500).nullable().default(null),
  publishedAt: z.string().datetime().nullable().default(null),
  html: z.string().max(8 * 1024 * 1024),
  text: z.string().max(2 * 1024 * 1024),
  assets: z.array(documentAssetSchema).max(100),
  quality: qualitySchema,
  warnings: z.array(warningSchema).max(100),
  extractorVersion: z.string().max(100),
  rulesVersion: z.string().max(100).optional(),
  rulesHash: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
  captureMethod: z.enum(['url', 'browser']),
});
export type CaptureDocument = z.infer<typeof documentSchema>;
export type DocumentAsset = z.infer<typeof documentAssetSchema>;
export const captureClientContextSchema = z
  .object({ entryPoint: z.enum(['ios_plus', 'ios_share', 'chrome', 'desktop', 'other']) })
  .strict();
export const captureInputSchema = z.object({
  mode: z.enum(['local', 'local_only', 'remote', 'link_only']).optional(),
  clientContext: captureClientContextSchema.optional(),
  url: z.string().min(1).max(16384),
  folderId: z.string().uuid().nullable().optional(),
  tagIds: z.array(z.string().uuid()).max(50).default([]),
  document: documentSchema.optional(),
  favorite: z.boolean().optional(),
});
export type CaptureInput = z.infer<typeof captureInputSchema>;
/** A local refresh cannot request a URL fetch or modify organization fields. */
export const localCaptureInputSchema = z
  .object({
    document: documentSchema,
    clientContext: captureClientContextSchema.optional(),
  })
  .strict();
export type LocalCaptureInput = z.infer<typeof localCaptureInputSchema>;
export const itemPatchSchema = z
  .object({
    title: z.string().min(1).max(1000).optional(),
    favorite: z.boolean().optional(),
    folderId: z.string().uuid().nullable().optional(),
    tagIds: z.array(z.string().uuid()).max(50).optional(),
    note: z.string().max(20000).optional(),
    expectedNoteVersion: z.number().int().nonnegative().optional(),
    trashed: z.boolean().optional(),
    mutationId: z.string().uuid().optional(),
  })
  .strict();
export type ItemPatch = z.infer<typeof itemPatchSchema>;
export const batchPatchInputSchema = z
  .object({
    ids: z.array(z.string().uuid()).min(1).max(100),
    patch: itemPatchSchema.default({}),
    tagOperation: z
      .object({
        mode: z.enum(['add', 'remove', 'replace']),
        tagIds: z.array(z.string().uuid()).max(50),
      })
      .strict()
      .optional(),
    mutationId: z.string().uuid().optional(),
  })
  .superRefine((value, context) => {
    if (value.tagOperation && value.patch.tagIds !== undefined)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tagOperation'],
        message: '标签操作不能与 patch.tagIds 同时提交',
      });
  });
export type BatchPatchInput = z.input<typeof batchPatchInputSchema>;
export type Folder = { id: string; name: string; parentId: string | null; version: number };
export type Tag = { id: string; name: string; color: string | null; version: number };
export type SearchMatch = {
  field: 'title' | 'note' | 'body' | 'author' | 'source' | 'tag';
  snippet: string;
  /** UTF-16 offsets in snippet; end is exclusive. No HTML is included. */
  ranges: { start: number; end: number }[];
};
export type LibraryItem = {
  id: string;
  url: string;
  /** Normalized hostname. Optional when reading older servers or offline records. */
  sourceHost?: string;
  identity: string;
  platform: Platform;
  kind: 'article' | 'post' | 'video';
  title: string;
  author: string | null;
  siteName: string | null;
  favorite: boolean;
  folderId: string | null;
  tagIds: string[];
  note: string;
  noteVersion: number;
  version: number;
  quality: 'complete' | 'partial' | 'link_only';
  status: 'queued' | 'running' | 'completed' | 'failed';
  warnings: { code: string; message: string }[];
  coverUrl: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  snapshotId: string | null;
  /** Present only in search responses; never persisted or included in item sync. */
  searchMatch?: SearchMatch;
};
export type SiteFaviconStatus = 'absent' | 'queued' | 'fetching' | 'ready' | 'unavailable';
/** Shared public site metadata; independent of article and account sync versions. */
export type SiteFaviconDescriptor = {
  host: string;
  status: SiteFaviconStatus;
  version: number;
  faviconUrl: string | null;
  hash: string | null;
  retryAt: string | null;
};
export type ItemDetail = LibraryItem & {
  /** Latest capture, or the scoped intent requested by captureJobId. No source HTML or credentials. */
  capture?: {
    id: string;
    mode: 'local' | 'local_only' | 'link_only' | 'remote';
    status: LibraryItem['status'];
    errorCode: string | null;
  } | null;
  snapshot: {
    id: string;
    document: CaptureDocument;
    createdAt: string;
    assets: { id: string; sourceId: string; url: string | null; status: string }[];
  } | null;
};
export { captureFailureNotice } from './capture-failure';
export type User = {
  id: string;
  email: string;
  name: string;
  verified: boolean;
  role: 'user' | 'admin';
};
export type CaptureReceipt = { item: LibraryItem; jobId: string | null; duplicate: boolean };
export type Page<T> = { items: T[]; nextCursor: string | null };
export const DEFAULT_LIMITS = {
  items: 1000,
  storageBytes: 1024 ** 3,
  capturesPerDay: 50,
  concurrentCaptures: 2,
  imagesPerCapture: 100,
  bytesPerCapture: 50 * 1024 ** 2,
  bytesPerImage: 12 * 1024 ** 2,
  trashDays: 30,
  syncDays: 90,
} as const;
